import { supabase } from "@/integrations/supabase/client";

export type CheckoutEventType =
  | "checkout_opened"
  | "provider_viewed"
  | "provider_selected"
  | "provider_changed"
  | "upsell_viewed"
  | "upsell_accepted"
  | "upsell_rejected"
  | "deposit_adjusted"
  | "plan_length_changed"
  | "checkout_abandoned"
  | "provider_redirected"
  | "booking_confirmed"
  | "booking_failed"
  | "bnpl_declined";

export interface CheckoutEvent {
  sessionId: string;
  userId: string | null;
  vertical: string;
  merchantId: string;
  amount: number;
  currency: string;
  event: CheckoutEventType;
  metadata: Record<string, unknown>;
  timestamp: string;
}

/**
 * Enrich checkout event metadata with the user's preferred BNPL provider
 * from user_preferences, if available. Fire-and-forget — never blocks.
 */
export async function getPreferredProvider(userId: string | null): Promise<string | null> {
  if (!userId) return null;
  try {
    const { data } = await supabase
      .from("user_preferences")
      .select("preferred_bnpl_provider")
      .eq("user_id", userId)
      .maybeSingle();
    return data?.preferred_bnpl_provider ?? null;
  } catch {
    return null;
  }
}

// Buffer events for 2s batch flushes
let buffer: CheckoutEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  if (buffer.length === 0) return;
  const batch = [...buffer];
  buffer = [];
  flushTimer = null;

  // Fire-and-forget
  supabase.functions
    .invoke("checkout-events", { body: { events: batch } })
    .catch((err) => console.error("checkout-events flush error", err));
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, 2000);
}

/** Generate a stable checkout session id */
export function generateCheckoutSessionId(): string {
  return `cs_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Track a checkout event — fire-and-forget, never blocks UI.
 */
export function trackCheckoutEvent(
  event: CheckoutEventType,
  data: Omit<CheckoutEvent, "event" | "timestamp">
): void {
  buffer.push({ ...data, event, timestamp: new Date().toISOString() });
  scheduleFlush();
}

// Flush on page unload
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flush);
}
